
import React from 'react';
import { useUser } from '@/contexts/UserContext';
import { useProfileData } from '@/hooks/useProfileData';
import PageLayout from '@/components/layout/PageLayout';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Heart, Settings } from 'lucide-react';
import { InviteCodeEditor } from '@/components/profile/editors/InviteCodeEditor';
import { FamilyManagement } from '@/components/profile/FamilyManagement';
import { FamilyMembersList } from '@/components/profile/FamilyMembersList';

const FamilyCircle: React.FC = () => {
  const { user } = useUser();
  const {
    profile,
    familyMembers,
    inviteCode,
    isFamilyAdmin,
    isSingleMemberFamily,
    canSwitchFamily,
    switchFamilyWarning,
    isLoading,
    error
  } = useProfileData();

  if (isLoading) {
    return (
      <PageLayout title="Family Circle" description="Collaborate with your partner on meal planning">
        <div className="animate-pulse space-y-6">
          <div className="h-48 bg-gray-200 rounded-lg"></div>
        </div>
      </PageLayout>
    );
  }

  if (error || !profile) {
    return (
      <PageLayout title="Family Circle" description="Collaborate with your partner on meal planning">
        <div className="p-4 text-center">
          <p className="text-lg text-red-500">{error || "Could not load family circle information."}</p>
        </div>
      </PageLayout>
    );
  }

  // Filter out the current user from family members for display
  const otherFamilyMembers = familyMembers ? familyMembers.filter(member => member.id !== user?.id) : [];
  const hasOtherFamilyMembers = otherFamilyMembers.length > 0;

  return (
    <PageLayout 
      title="Family Circle" 
      description="Collaborate with your partner on meal planning"
    >
      <div className="pb-14">
        <div className="space-y-6">
        {/* Invite Code Section */}
        <Card>
          <CardHeader className="bg-sage/5 border-b border-sage/10 pb-4">
            <CardTitle className="flex items-center gap-2 text-sage">
              <Heart className="h-5 w-5" />
              Invite code
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="bg-gradient-to-r from-sage/5 to-golden/5 p-4 rounded-lg border border-sage/20">
              <p className="text-sm text-muted-foreground mb-4">
                Invite your partner or family members to collaborate on meal planning! Share recipes, 
                create meal plans together, and enjoy a more organized family cooking experience.
              </p>
              
              <div className="space-y-3">
                <InviteCodeEditor
                  userId={user?.id}
                  inviteCode={inviteCode}
                  isEditable={!!profile.current_family_id}
                  isFamilyAdmin={isFamilyAdmin}
                />
                
                {isSingleMemberFamily && (
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium">
                      💡 You're the only member in your Family circle. Share your invite code with your partner 
                      to start collaborating on meal planning together!
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Family Circle Management */}
        {profile.current_family_id && (
          <Card>
            <CardHeader>
              <CardTitle>Family circle management</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Family Members Section */}
              {hasOtherFamilyMembers && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Family Members</h3>
                  <FamilyMembersList
                    familyMembers={otherFamilyMembers}
                    isFamilyAdmin={isFamilyAdmin}
                    userId={user?.id}
                    familyId={profile.current_family_id}
                    familyAdminId={profile.registration_family_id === profile.current_family_id ? user?.id : undefined}
                  />
                </div>
              )}
              
              {/* Admin Section */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin</h3>
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {isSingleMemberFamily 
                      ? "Join a Family circle to start collaborating on recipes, planning and shopping."
                      : "To join a Family circle, remove first all members of yours."
                    }
                  </p>
                  <FamilyManagement
                    profile={profile}
                    userId={user?.id!}
                    isFamilyAdmin={isFamilyAdmin}
                    isSingleMemberFamily={isSingleMemberFamily}
                    inviteCode={inviteCode}
                    canSwitchFamily={canSwitchFamily}
                    switchFamilyWarning={switchFamilyWarning}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        </div>
      </div>
    </PageLayout>
  );
};

export default FamilyCircle;
